/**
 * The Turnstile widget. The first client-side challenge this game has ever rendered.
 *
 * `server/src/routes/auth.ts` has called `assertTurnstile` on every signup since Phase 1 and
 * `game/src/game/session.ts` sent `turnstile: ''` with a comment admitting it, so signup could
 * only ever succeed where the secret short-circuits. This component is the missing half.
 *
 * Four things about the Turnstile API drive the shape of this file, and three of them fail quietly.
 *
 *  1. **A token is SINGLE USE and expires after 300 seconds.** A refused signup burns it, so the
 *     second attempt at a taken handle would fail on the challenge rather than on the handle, and
 *     the player would read the wrong reason. `refresh()` exists for exactly that: the caller
 *     resets the widget after every attempt, landed or refused.
 *  2. **`action` is asserted on the server.** `verifyTurnstile` compares the siteverify response's
 *     `action` against the one the endpoint expects, so a token minted by the signup widget cannot
 *     be spent on password reset. Passing it here is what makes that assertion mean anything.
 *  3. **Explicit rendering, not the `cf-turnstile` class.** Cloudflare recommends it for
 *     single-page applications, and the automatic mode scans the DOM once at script load: a widget
 *     React mounts later is never found, which looks exactly like the script failing to load.
 *  4. **The script is loaded ONCE per document.** A second `<script>` for the same API is not an
 *     error and not a duplicate widget; it is a second copy of a global that the first one already
 *     owns. The module-level promise below is the whole reason this is a module and not a hook.
 *
 * `execution: 'render'` and `appearance: 'always'` are deliberate. The alternative hides the widget
 * until a challenge is needed, which means the one visible sign that a bot defence exists on this
 * page disappears for the people it is working on. The login-page promise in `Gate.tsx` is a
 * contract about what this game does to you, and a silent challenge is the wrong side of it.
 */

import { useCallback, useEffect, useRef } from 'react'

/** The subset of the global this file uses. Cloudflare ships no types for it. */
interface TurnstileApi {
  render: (el: HTMLElement, opts: Record<string, unknown>) => string | undefined
  reset: (id?: string) => void
  remove: (id?: string) => void
  getResponse: (id?: string) => string | undefined
}

declare global {
  interface Window {
    turnstile?: TurnstileApi
  }
}

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'

let loading: Promise<TurnstileApi> | null = null

/**
 * Load the API exactly once per document and resolve with the global.
 *
 * It REJECTS on a load failure rather than hanging, because a corporate proxy or a blocked-script
 * extension is a real and common state, and a signup form that spins forever is the worst possible
 * answer to it. The Gate turns the rejection into a sentence.
 */
function loadTurnstile(): Promise<TurnstileApi> {
  if (window.turnstile) return Promise.resolve(window.turnstile)
  if (loading) return loading
  loading = new Promise<TurnstileApi>((resolve, reject) => {
    const el = document.createElement('script')
    el.src = SCRIPT_SRC
    el.async = true
    el.defer = true
    el.onload = () => {
      if (window.turnstile) resolve(window.turnstile)
      else reject(new Error('turnstile loaded without its global'))
    }
    el.onerror = () => {
      // Cleared so a later mount can try again. A player who turns an extension off and reloads
      // the panel should not be held to the first failure for the lifetime of the tab.
      loading = null
      reject(new Error('turnstile script did not load'))
    }
    document.head.appendChild(el)
  })
  return loading
}

export interface TurnstileHandle {
  /** Burn the current token and ask for a fresh one. Call after EVERY submit. */
  refresh: () => void
}

export function Turnstile({
  sitekey,
  action,
  onToken,
  onFault,
  handle,
}: {
  sitekey: string
  /** Asserted against the siteverify response by the server. `signup` or `reset`. */
  action: string
  onToken: (token: string) => void
  /** Called with a sentence a player can read when the widget cannot run at all. */
  onFault: (message: string) => void
  handle?: React.RefObject<TurnstileHandle | null>
}) {
  const host = useRef<HTMLDivElement | null>(null)
  const widget = useRef<string | null>(null)
  /*
   * The callbacks are held in refs and the effect depends on neither.
   *
   * `onToken` is a fresh closure on every Gate render, and the Gate re-renders on every keystroke
   * in the email field. An effect that depended on it would remove and re-render the widget on each
   * one, which means a challenge that never finishes and a token that never arrives.
   */
  const token = useRef(onToken)
  const fault = useRef(onFault)
  token.current = onToken
  fault.current = onFault

  useEffect(() => {
    if (sitekey === '') return
    let dead = false
    let mine: string | null = null

    void loadTurnstile().then(
      (api) => {
        if (dead || !host.current) return
        // React 18 and later mount effects twice under StrictMode in development. Rendering into
        // a host that already holds a widget produces two challenges stacked on each other, so
        // the cleanup below removes by id and this guards the window between the two.
        if (widget.current !== null) return
        const id = api.render(host.current, {
          sitekey,
          /*
           * `action` here IS the `data-action` attribute the declarative mode takes: explicit
           * rendering passes as an option what the `cf-turnstile` div would carry as an attribute.
           * It rides on the siteverify response and `server/src/lib/turnstile.ts` asserts it, and
           * it is also what the Turnstile dashboard attributes an integration by.
           */
          action,
          theme: 'dark',
          appearance: 'always',
          execution: 'render',
          size: 'flexible',
          callback: (t: string) => token.current(t),
          'error-callback': () => {
            fault.current('The check would not run. Reload the page and try again.')
            // Returning true would suppress Cloudflare's own retry. It is left to retry.
            return false
          },
          'expired-callback': () => api.reset(id),
          'timeout-callback': () => api.reset(id),
        })
        if (id === undefined) {
          fault.current('The check would not start. Reload the page and try again.')
          return
        }
        mine = id
        widget.current = id
      },
      () => {
        if (!dead) {
          fault.current(
            'The security check could not load. An extension or a network filter is usually why.',
          )
        }
      },
    )

    return () => {
      dead = true
      const id = mine ?? widget.current
      if (id !== null && window.turnstile) window.turnstile.remove(id)
      widget.current = null
    }
  }, [sitekey, action])

  const refresh = useCallback(() => {
    if (widget.current !== null && window.turnstile) window.turnstile.reset(widget.current)
  }, [])

  useEffect(() => {
    if (handle) handle.current = { refresh }
  }, [handle, refresh])

  if (sitekey === '') return null
  return <div ref={host} style={{ marginBottom: 12, minHeight: 65 }} />
}
